import React, { Suspense } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import CatererProtectedRoute from './ProtectedRoute';
import CatererSEO from './components/CatererSEO';

// Auth Pages (eager for fastest initial interaction)
import Login from './pages/auth/Login';
import CatererLanding from './pages/landing';

// Footer / Public Pages (lazy)
const ContactUs = React.lazy(() => import('./pages/contact-us'));
const SubmitIdea = React.lazy(() => import('./pages/submit-idea'));
const ReportProblem = React.lazy(() => import('./pages/report-problem'));
const ResetPassword = React.lazy(() => import('./pages/reset-password'));
const DeleteAccount = React.lazy(() => import('./pages/delete-account'));
const TermsOfService = React.lazy(() => import('./pages/terms-of-service'));
const PrivacyPolicy = React.lazy(() => import('./pages/privacy-policy'));
const RefundPolicy = React.lazy(() => import('./pages/refund-policy'));
const AcceptableUsePolicy = React.lazy(() => import('./pages/acceptable-use-policy'));
const ShippingPolicy = React.lazy(() => import('./pages/shipping-policy'));
const ReturnsPolicy = React.lazy(() => import('./pages/returns-policy'));
const Compliance = React.lazy(() => import('./pages/compliance'));
const CatererVerification = React.lazy(() => import('./pages/caterer-verification'));
const TrustScore = React.lazy(() => import('./pages/trust-score'));
const Payments = React.lazy(() => import('./pages/payments'));

// Caterer Pages (lazy to reduce first-load bundle)
const CatererOnboarding = React.lazy(() => import('./pages/onboarding'));
const CatererDashboard = React.lazy(() => import('./pages/dashboard'));
const MenuManagement = React.lazy(() => import('./pages/menu-management'));
const BidManagement = React.lazy(() => import('./pages/bid-management'));
const Orders = React.lazy(() => import('./pages/orders'));
const Portfolio = React.lazy(() => import('./pages/portfolio'));
const Wallet = React.lazy(() => import('./pages/wallet'));
const Reviews = React.lazy(() => import('./pages/reviews'));
const BusinessProfile = React.lazy(() => import('./pages/business-profile'));
const VerificationUpgrade = React.lazy(() => import('./pages/verification/upgrade'));
const VerificationSuccess = React.lazy(() => import('./pages/verification/success'));
const Messages = React.lazy(() => import('./pages/messages'));
const SponsoredPlacements = React.lazy(() => import('./pages/sponsored-placements'));
const ToolsHub = React.lazy(() => import('./pages/tools'));
const RecipeCalculator = React.lazy(() => import('./pages/tools/RecipeCalculator'));
const Invoicing = React.lazy(() => import('./pages/tools/Invoicing'));
const KitchenDisplay = React.lazy(() => import('./pages/tools/KitchenDisplay'));
const AccountStatements = React.lazy(() => import('./pages/tools/AccountStatements'));
const LeadResponseTemplates = React.lazy(() => import('./pages/tools/LeadResponseTemplates'));
const EventProductionPlanner = React.lazy(() => import('./pages/tools/EventProductionPlanner'));
const ProfitSnapshot = React.lazy(() => import('./pages/tools/ProfitSnapshot'));

/**
 * All routes for caterer users
 * Base path: /caterer
 */
export default function CatererRoutes() {
  return (
    <>
      <CatererSEO />
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading caterer experience...</p>
            </div>
          </div>
        }
      >
      <Routes>
      {/* Auth Routes */}
      <Route path="/" element={<CatererLanding />} />
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Public Caterer Routes */}
      <Route path="/onboarding" element={<CatererOnboarding />} />
      
      {/* Protected Caterer Routes */}
      <Route
        path="/dashboard"
        element={
          <CatererProtectedRoute>
            <CatererDashboard />
          </CatererProtectedRoute>
        }
      />
      
      <Route
        path="/orders"
        element={
          <CatererProtectedRoute>
            <Orders />
          </CatererProtectedRoute>
        }
      />
      
      <Route
        path="/menu-management"
        element={
          <CatererProtectedRoute>
            <MenuManagement />
          </CatererProtectedRoute>
        }
      />
      
      <Route
        path="/bid-management"
        element={
          <CatererProtectedRoute>
            <BidManagement />
          </CatererProtectedRoute>
        }
      />
      
      <Route
        path="/portfolio"
        element={
          <CatererProtectedRoute>
            <Portfolio />
          </CatererProtectedRoute>
        }
      />
      
      <Route
        path="/wallet"
        element={
          <CatererProtectedRoute>
            <Wallet />
          </CatererProtectedRoute>
        }
      />
      
      <Route
        path="/reviews"
        element={
          <CatererProtectedRoute>
            <Reviews />
          </CatererProtectedRoute>
        }
      />
      
      <Route
        path="/messages"
        element={
          <CatererProtectedRoute>
            <Messages />
          </CatererProtectedRoute>
        }
      />
      
      <Route
        path="/sponsored-placements"
        element={
          <CatererProtectedRoute>
            <SponsoredPlacements />
          </CatererProtectedRoute>
        }
      />

      {/* Tools Routes */}
      <Route
        path="/tools"
        element={
          <CatererProtectedRoute>
            <ToolsHub />
          </CatererProtectedRoute>
        }
      />
      <Route
        path="/tools/calculator"
        element={
          <CatererProtectedRoute>
            <RecipeCalculator />
          </CatererProtectedRoute>
        }
      />
      <Route
        path="/tools/invoicing"
        element={
          <CatererProtectedRoute>
            <Invoicing />
          </CatererProtectedRoute>
        }
      />
      <Route
        path="/tools/kds"
        element={
          <CatererProtectedRoute>
            <KitchenDisplay />
          </CatererProtectedRoute>
        }
      />
      <Route
        path="/tools/statements"
        element={
          <CatererProtectedRoute>
            <AccountStatements />
          </CatererProtectedRoute>
        }
      />
      <Route
        path="/tools/lead-templates"
        element={
          <CatererProtectedRoute>
            <LeadResponseTemplates />
          </CatererProtectedRoute>
        }
      />
      <Route
        path="/tools/production-planner"
        element={
          <CatererProtectedRoute>
            <EventProductionPlanner />
          </CatererProtectedRoute>
        }
      />
      <Route
        path="/tools/profit-snapshot"
        element={
          <CatererProtectedRoute>
            <ProfitSnapshot />
          </CatererProtectedRoute>
        }
      />
      
      <Route
        path="/business-profile"
        element={
          <CatererProtectedRoute>
            <BusinessProfile />
          </CatererProtectedRoute>
        }
      />
      
      <Route
        path="/verification/upgrade"
        element={
          <CatererProtectedRoute>
            <VerificationUpgrade />
          </CatererProtectedRoute>
        }
      />
      
      <Route
        path="/verification/success"
        element={
          <CatererProtectedRoute>
            <VerificationSuccess />
          </CatererProtectedRoute>
        }
      />
      
      
      {/* Default redirect */}
      <Route path="/contact-us" element={<ContactUs />} />
      <Route path="/submit-idea" element={<SubmitIdea />} />
      <Route path="/report-problem" element={<ReportProblem />} />
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/acceptable-use-policy" element={<AcceptableUsePolicy />} />
      <Route path="/shipping-policy" element={<ShippingPolicy />} />
      <Route path="/returns-policy" element={<ReturnsPolicy />} />
      <Route path="/compliance" element={<Compliance />} />
      <Route path="/caterer-verification" element={<CatererVerification />} />
      <Route path="/trust-score" element={<TrustScore />} />
      <Route path="/payments" element={<Payments />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
      </Suspense>
    </>
  );
}
